/**
 * The synthesised voices. Every sound is generated at play time from oscillators and filtered
 * noise — no recorded file ships, so there is nothing to license, nothing to download, and the
 * sounds work offline from the first load (spec §Sourcing). Each voice is a small function taking
 * the context, its destination bus and a start time, plus a few parameters; `detune` (a per-play
 * random from the engine) is what keeps repeated moves from sounding mechanical.
 *
 * The voices never touch `ctx.currentTime` themselves: the engine passes `t0` so tests can
 * schedule against a fake context deterministically.
 */

export interface VoiceParams {
  /** -1 (left) .. 1 (right); 0 is centre. Optional, mono-safe. */
  readonly pan?: number;
  /** Random per-play variation, roughly -1..1. Voices shift pitch/brightness by a few percent. */
  readonly detune?: number;
}

type Ctx = BaseAudioContext;

/** A short filtered noise burst — the raw material for slaps, rattles and swishes. */
export function noiseBurst(ctx: Ctx, dest: AudioNode, t0: number, opts: { dur: number; freq: number; q: number; gain: number; detune?: number }): void {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * opts.dur));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = opts.freq * (1 + (opts.detune ?? 0) * 0.06);
  filter.Q.value = opts.q;
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(opts.gain, t0);
  amp.gain.exponentialRampToValueAtTime(0.001, t0 + opts.dur);
  src.connect(filter).connect(amp).connect(dest);
  src.start(t0);
  src.stop(t0 + opts.dur);
}

/** A struck-wood "tock": a sine that drops in pitch, with a noise tick on the attack. */
export function woodTock(ctx: Ctx, dest: AudioNode, t0: number, opts: { freq: number; dur?: number; gain?: number } & VoiceParams): void {
  const dur = opts.dur ?? 0.09;
  const d = opts.detune ?? 0;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  const f = opts.freq * (1 + d * 0.05);
  osc.frequency.setValueAtTime(f, t0);
  osc.frequency.exponentialRampToValueAtTime(f * 0.55, t0 + dur);
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(opts.gain ?? 0.5, t0);
  amp.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  osc.connect(amp).connect(dest);
  osc.start(t0);
  osc.stop(t0 + dur + 0.01);
  noiseBurst(ctx, dest, t0, { dur: 0.02, freq: 2400, q: 1.2, gain: 0.12 * (opts.gain ?? 0.5) / 0.5, detune: d });
}

/** A soft card slap: a bandpassed noise burst, brighter and shorter than a piece move. */
export function cardSlap(ctx: Ctx, dest: AudioNode, t0: number, opts: VoiceParams & { gain?: number }): void {
  noiseBurst(ctx, dest, t0, { dur: 0.06, freq: 1300, q: 0.9, gain: opts.gain ?? 0.4, detune: opts.detune });
}

/** A rattle of short noise bursts: dice in a cup, then landing. */
export function diceRattle(ctx: Ctx, dest: AudioNode, t0: number, opts: VoiceParams): void {
  const d = opts.detune ?? 0;
  const times = [0, 0.07, 0.15, 0.26, 0.4];
  times.forEach((offset, i) => {
    noiseBurst(ctx, dest, t0 + offset, { dur: 0.035, freq: 2000 + i * 250 * (1 + d * 0.1), q: 1.5, gain: 0.22 - i * 0.03 });
  });
  // the land: one lower, firmer tap
  woodTock(ctx, dest, t0 + 0.5, { freq: 190, dur: 0.07, gain: 0.4, detune: d });
}

/** A plucked string: a sine with a fast decay and a soft second partial. */
export function pluck(ctx: Ctx, dest: AudioNode, t0: number, freq: number, opts: { gain?: number; dur?: number; detune?: number } = {}): void {
  const d = opts.detune ?? 0;
  const f = freq * (1 + d * 0.04);
  const dur = opts.dur ?? 0.5;
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(opts.gain ?? 0.22, t0);
  amp.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  for (const [mult, g] of [[1, 1], [2, 0.35]] as const) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = f * mult;
    const partial = ctx.createGain();
    partial.gain.value = g;
    osc.connect(partial).connect(amp);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }
  amp.connect(dest);
}

/** A rising or falling arpeggio of plucks — win, lose, level-up, a made contract. */
export function arpeggio(ctx: Ctx, dest: AudioNode, t0: number, freqs: readonly number[], opts: { step?: number; gain?: number; detune?: number } = {}): void {
  const step = opts.step ?? 0.12;
  freqs.forEach((freq, i) => {
    pluck(ctx, dest, t0 + i * step, freq, { gain: opts.gain ?? 0.2, dur: 0.45, detune: opts.detune });
  });
}

/** A soft two-note "uh-oh": an illegal move. Deliberately not a buzzer. */
export function softDeny(ctx: Ctx, dest: AudioNode, t0: number, opts: VoiceParams = {}): void {
  const d = opts.detune ?? 0;
  pluck(ctx, dest, t0, 220 * (1 + d * 0.03), { gain: 0.16, dur: 0.18 });
  pluck(ctx, dest, t0 + 0.11, 196 * (1 + d * 0.03), { gain: 0.16, dur: 0.24 });
}

/** A swish with a rising filter — a swept-up trick, a dealt hand. */
export function swish(ctx: Ctx, dest: AudioNode, t0: number, opts: VoiceParams & { dur?: number; gain?: number }): void {
  const dur = opts.dur ?? 0.3;
  const frames = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 1.1;
  filter.frequency.setValueAtTime(500, t0);
  filter.frequency.exponentialRampToValueAtTime(3200, t0 + dur);
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, t0);
  amp.gain.exponentialRampToValueAtTime(opts.gain ?? 0.25, t0 + dur * 0.35);
  amp.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(filter).connect(amp).connect(dest);
  src.start(t0);
  src.stop(t0 + dur);
}
